/**
 * GuardrailPanel — the UP-F19 live guardrail checklist for the upload wizard (V3 skin).
 *
 * WHAT IT IS. A read-only checklist of the nine platform guardrails GC-1..GC-9 that a submission must
 * satisfy, each row ticking, warning, or sitting PENDING as the builder fills the wizard. It makes the
 * governance the server enforces VISIBLE while the builder works, so nothing about why a tool would be
 * held is a surprise at submit time. Ticks are INFORMATIVE, never a gate: consistent with UP-F45, the
 * submit button stays enabled and pressing it with items outstanding focuses the first offender (see
 * UploadReviewPage / useUploadOverlay). This panel never disables anything.
 *
 * WHERE ITS STATE COMES FROM. Every row is derived from state the wizard already holds — no new source
 * of truth, so the panel cannot disagree with the actual gates:
 *   - GC-1..GC-5, GC-9 come from the Details validation (`validateDetails` → DetailsErrors), the same
 *     errors the Details stage reddens its fields from. A guard passes exactly when its field(s) carry
 *     no error.
 *   - GC-7 (the data-safety attestation) is `draft.attested`, the same tick the Review footer gates on.
 *   - GC-6 (the artefact is valid for its type) and GC-8 (no blocked file type) become live once an
 *     artefact has been chosen. Until then they are PENDING (not failing) — the guard is real but not
 *     yet evaluable, which the spec calls for: "where a check cannot yet be evaluated, show it as
 *     pending rather than false." The artefact-type selector and the file input are C1's; this panel
 *     reads the small `ArtefactGuardState` they produce (see below), so File/Prompt/Link each light
 *     GC-6 the moment their input is valid, and a file with a blocked/mismatched extension fails GC-8.
 *
 * CLIENT vs SERVER. The file-extension and magic-byte checks (GC-6/GC-8) are a CLIENT HINT here — the
 * server (routes/tools.ts artefact handler) re-reads the blob from disk and re-sniffs it, and its 422s
 * are the authority. This panel's job is to surface the guardrails early, not to be the gate.
 */
import type { DetailsErrors } from './validation';

/** The three coarse states a guard row can be in. `pending` = real but not yet evaluable. */
export type GuardStatus = 'pass' | 'fail' | 'pending';

/** One rendered guardrail row. `code` is the GC-n label; `status` drives the glyph and tone. */
export interface GuardCheck {
  code: string;
  label: string;
  status: GuardStatus;
}

/**
 * The artefact-side facts the panel needs for GC-6 and GC-8, produced by C1's artefact view-model
 * (the artefact-type selector + File/Prompt/Link inputs). Kept deliberately small and presence-based
 * so this panel does not reach into the file-input internals:
 *
 *   - `type`     the chosen artefact type, or null while the builder has not chosen one yet.
 *   - `valid`    GC-6: is the artefact valid FOR ITS TYPE? For file: a primary file is present with an
 *                allowed extension and a passing magic-byte hint. For prompt: prompt_text >= 20 (see
 *                ArtefactPromptInput.isPromptValid). For link: a well-formed https URL (see
 *                ArtefactLinkInput.isLinkValid). null while it cannot be judged (no artefact chosen /
 *                no file added).
 *   - `hasBlockedFile` GC-8: for the file type, does any chosen file carry a blocked or disallowed
 *                extension? Only meaningful for files; leave false/undefined for prompt and link.
 *
 * All fields optional so the panel renders correctly with NO artefact prop at all (both artefact rows
 * pending) — the state before C1's selector has been wired, and the Details-only view.
 */
export interface ArtefactGuardState {
  type?: 'file' | 'prompt' | 'link' | null;
  valid?: boolean | null;
  hasBlockedFile?: boolean;
}

export interface GuardrailInputs {
  /** The Details-stage validation result (validateDetails). A guard passes when its fields are clean. */
  detailErrors: DetailsErrors;
  /** The data-safety attestation tick (GC-7). */
  attested: boolean;
  /** The artefact-side facts for GC-6/GC-8; omit for a Details-only view (both artefact rows pending). */
  artefact?: ArtefactGuardState;
}

/** A Details guard passes when NONE of the fields it covers carries a validation error. */
function fieldsClean(errors: DetailsErrors, fields: (keyof DetailsErrors)[]): boolean {
  return fields.every((f) => errors[f] === undefined);
}

/**
 * Derive the nine guardrail rows from wizard state. Pure — no React, no DOM — so the mapping can be
 * reasoned about and tested on its own, the same separation validation.ts keeps. The GC numbering and
 * scope follow the architect spec's guardrail list.
 */
export function deriveGuardChecks(inputs: GuardrailInputs): GuardCheck[] {
  const { detailErrors, attested, artefact } = inputs;
  const art = artefact ?? {};

  // GC-6 (artefact valid for its type) and GC-8 (no blocked file type) are only evaluable once an
  // artefact type has been chosen. Before that they are pending, not failing.
  let gc6: GuardStatus = 'pending';
  let gc8: GuardStatus = 'pending';
  if (art.type) {
    // GC-6: valid===true passes, valid===false fails, null/undefined (chosen a type but not yet a
    // valid input — e.g. an empty prompt, or file selected but none added) stays pending.
    gc6 = art.valid === true ? 'pass' : art.valid === false ? 'fail' : 'pending';
    // GC-8 only bites for files; prompt/link have no file to block, so the guard is satisfied for them.
    if (art.type === 'file') {
      gc8 = art.hasBlockedFile ? 'fail' : 'pass';
    } else {
      gc8 = 'pass';
    }
  }

  const bool = (ok: boolean): GuardStatus => (ok ? 'pass' : 'fail');

  return [
    {
      code: 'GC-1',
      label: 'Name and description provided',
      status: bool(fieldsClean(detailErrors, ['name', 'description'])),
    },
    {
      code: 'GC-2',
      label: 'Problem it solves is stated',
      status: bool(fieldsClean(detailErrors, ['problemSolved'])),
    },
    {
      code: 'GC-3',
      label: 'Category, access and department chosen',
      status: bool(fieldsClean(detailErrors, ['categoryId', 'accessId', 'departmentId'])),
    },
    {
      code: 'GC-4',
      label: 'Time saved is between 1 and 600 minutes',
      status: bool(fieldsClean(detailErrors, ['estimatedTimeSaved'])),
    },
    {
      code: 'GC-5',
      label: 'Dependencies listed (write “None” if there are none)',
      status: bool(fieldsClean(detailErrors, ['dependencies'])),
    },
    { code: 'GC-6', label: 'Artefact is valid for the chosen type', status: gc6 },
    { code: 'GC-7', label: 'Data-safety attestation confirmed', status: bool(attested) },
    { code: 'GC-8', label: 'No blocked file type', status: gc8 },
    {
      // The three classifications come from governed lists (0004), so "chosen" IS "governed" — GC-9 is
      // satisfied by the same clean-classification state as GC-3, surfaced as its own row because the
      // guardrail it names (governed vocabulary) is distinct.
      code: 'GC-9',
      label: 'Governed classifications used',
      status: bool(fieldsClean(detailErrors, ['categoryId', 'accessId', 'departmentId'])),
    },
  ];
}

/** The glyph + tone for each status — reusing the wizard's own ✓ / ● convention (InstructionsStage). */
function statusPresentation(status: GuardStatus): { glyph: string; tone: string; word: string } {
  switch (status) {
    case 'pass':
      return { glyph: '✓', tone: 'text-ink', word: 'Passed' };
    case 'fail':
      return { glyph: '●', tone: 'text-error', word: 'Not yet met' };
    case 'pending':
    default:
      return { glyph: '○', tone: 'text-text-inactive', word: 'Pending' };
  }
}

/**
 * The rendered panel. `title` defaults to the section heading the Review step wants; pass a different
 * one for a side-rail placement. Rows are read-only; the whole panel is aria-labelled as a checklist.
 */
export function GuardrailPanel({
  inputs,
  title = 'Guardrails',
  className = '',
}: {
  inputs: GuardrailInputs;
  title?: string;
  className?: string;
}) {
  const checks = deriveGuardChecks(inputs);
  const passed = checks.filter((c) => c.status === 'pass').length;

  return (
    <section
      aria-label="Submission guardrails"
      className={['flex min-w-0 flex-col gap-row-gap', className].join(' ')}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-xs font-medium text-ink">{title}</h3>
        {/* A quiet running tally — informative, in the inactive tone so it does not read as a score. */}
        <span className="shrink-0 text-xs font-light text-text-inactive">
          {passed}/{checks.length} met
        </span>
      </div>

      <ul className="flex min-w-0 flex-col gap-2">
        {checks.map((check) => {
          const { glyph, tone, word } = statusPresentation(check.status);
          return (
            <li key={check.code} className="flex min-w-0 items-start gap-2 text-xs font-light">
              {/* The glyph carries the status word for screen readers; the visible glyph is aria-hidden. */}
              <span aria-hidden="true" className={['shrink-0 leading-5', tone].join(' ')}>
                {glyph}
              </span>
              <span className="sr-only">{word}: </span>
              <span
                className={[
                  'min-w-0',
                  // A failed row leads the eye in the error tone; passed/pending stay ink so the list
                  // is not a wall of red while the builder is still filling it in.
                  check.status === 'fail' ? 'text-error' : 'text-ink',
                ].join(' ')}
              >
                {check.label}
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
